/**
 * B2COrderEditDialog — edit a NEW (pre-AWB) B2C order. Shared by the seller and
 * admin/warehouse order-detail pages. Everything the order carried from create is
 * editable (buyer, products, package, value, payment/COD, pickup, reference). The
 * backend (PUT) 409s once an AWB is assigned and records a field-level edit_history
 * entry; this dialog is only shown while status === "NEW".
 *
 * Props:
 *   order            — the current order doc
 *   saveUrl          — PUT endpoint (e.g. `${API}/b2c/admin/orders/${id}` or seller)
 *   pickupUrl        — GET for this seller's pickup locations
 *   open, onOpenChange, onSaved(updatedOrder)
 */
import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const emptyProduct = () => ({ name: "", sku: "", unit_price: "", qty: 1, discount: "", tax_pct: "", hsn: "" });
const num = (v) => (v === "" || v === null || v === undefined ? "" : v);
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export function B2COrderEditDialog({ order, saveUrl, pickupUrl, open, onOpenChange, onSaved }) {
  const [buyer, setBuyer] = useState({});
  const [products, setProducts] = useState([emptyProduct()]);
  const [pkg, setPkg] = useState({ dead_weight_kg: "", length_cm: "", breadth_cm: "", height_cm: "" });
  const [paymentMode, setPaymentMode] = useState("prepaid");
  const [codAmount, setCodAmount] = useState("");
  const [orderValue, setOrderValue] = useState("");
  const [reference, setReference] = useState("");
  const [pickupLocationId, setPickupLocationId] = useState("");
  const [pickupLocations, setPickupLocations] = useState([]);
  const [saving, setSaving] = useState(false);

  // Seed the form from the order each time the dialog opens.
  useEffect(() => {
    if (!open || !order) return;
    const b = order.buyer || {};
    setBuyer({
      name: b.name || "", phone: b.phone || "", email: b.email || "", address: b.address || "",
      landmark: b.landmark || "", city: b.city || "", state: b.state || "", pincode: b.pincode || "",
    });
    setProducts(
      Array.isArray(order.products) && order.products.length
        ? order.products.map((p) => ({ ...emptyProduct(), ...p }))
        : [emptyProduct()]
    );
    const pk = order.package || {};
    setPkg({
      dead_weight_kg: num(pk.dead_weight_kg), length_cm: num(pk.length_cm),
      breadth_cm: num(pk.breadth_cm), height_cm: num(pk.height_cm),
    });
    setPaymentMode(order.payment_mode || "prepaid");
    setCodAmount(num(order.cod_amount));
    setOrderValue(num(order.order_value));
    setReference(order.reference_number || "");
    setPickupLocationId(order.pickup_location_id || "");
  }, [open, order]);

  // Load this seller's pickup locations for the dropdown.
  useEffect(() => {
    if (!open || !pickupUrl) return;
    axios.get(pickupUrl)
      .then((res) => setPickupLocations(Array.isArray(res.data) ? res.data : res.data?.items || []))
      .catch(() => setPickupLocations([]));
  }, [open, pickupUrl]);

  const computedValue = useMemo(() => round2(products.reduce((sum, p) => {
    const gross = (parseFloat(p.unit_price) || 0) * (parseInt(p.qty, 10) || 0);
    const afterDisc = Math.max(0, gross - (parseFloat(p.discount) || 0));
    return sum + afterDisc * (1 + (parseFloat(p.tax_pct) || 0) / 100);
  }, 0)), [products]);

  const setBuyerField = (k, v) => setBuyer((b) => ({ ...b, [k]: v }));
  const setProductField = (i, k, v) => setProducts((prev) => prev.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)));
  const addProduct = () => setProducts((prev) => [...prev, emptyProduct()]);
  const removeProduct = (i) => setProducts((prev) => (prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev));

  const handleSave = async () => {
    if (!buyer.name?.trim()) return toast.error("Buyer name is required");
    if (String(buyer.phone || "").replace(/\D/g, "").length < 10) return toast.error("Buyer phone must be a 10-digit mobile");
    if (!buyer.address?.trim()) return toast.error("Buyer address is required");
    if (String(buyer.pincode || "").replace(/\D/g, "").length !== 6) return toast.error("Buyer pincode must be 6 digits");
    if (!pickupLocationId) return toast.error("Select a pickup location");
    for (const p of products) {
      if (!p.name?.trim()) return toast.error("Every product needs a name");
      if (!(parseFloat(p.unit_price) >= 0)) return toast.error("Every product needs a unit price");
      if (!(parseInt(p.qty, 10) > 0)) return toast.error("Every product needs a quantity");
    }
    for (const [k, label] of [["dead_weight_kg", "Dead weight"], ["length_cm", "Length"], ["breadth_cm", "Breadth"], ["height_cm", "Height"]]) {
      if (!(parseFloat(pkg[k]) > 0)) return toast.error(`${label} must be greater than 0`);
    }
    if (paymentMode === "cod" && !(parseFloat(codAmount) >= 0)) return toast.error("COD amount is required for COD orders");

    const payload = {
      buyer: {
        name: buyer.name.trim(), phone: buyer.phone, email: (buyer.email || "").trim() || undefined,
        address: buyer.address.trim(), landmark: (buyer.landmark || "").trim() || undefined,
        city: (buyer.city || "").trim() || undefined, state: (buyer.state || "").trim() || undefined,
        pincode: buyer.pincode,
      },
      products: products.map((p) => ({
        name: p.name.trim(), sku: (p.sku || "").trim() || undefined,
        unit_price: parseFloat(p.unit_price) || 0, qty: parseInt(p.qty, 10) || 0,
        discount: p.discount === "" ? undefined : parseFloat(p.discount) || 0,
        tax_pct: p.tax_pct === "" ? undefined : parseFloat(p.tax_pct) || 0,
        hsn: (p.hsn || "").trim() || undefined,
      })),
      package: {
        dead_weight_kg: parseFloat(pkg.dead_weight_kg), length_cm: parseFloat(pkg.length_cm),
        breadth_cm: parseFloat(pkg.breadth_cm), height_cm: parseFloat(pkg.height_cm),
      },
      payment_mode: paymentMode,
      order_value: orderValue === "" ? undefined : parseFloat(orderValue),
      pickup_location_id: pickupLocationId,
      reference_number: reference.trim() || null,
    };
    if (paymentMode === "cod") payload.cod_amount = parseFloat(codAmount);

    setSaving(true);
    try {
      const res = await axios.put(saveUrl, payload);
      toast.success("Order updated");
      onSaved?.(res.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update order");
    } finally {
      setSaving(false);
    }
  };

  const field = (label, value, onChange, props = {}) => (
    <div>
      <Label className="text-xs text-slate-600">{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 h-9" {...props} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit order {order?.order_number}</DialogTitle>
          <DialogDescription>
            Editable until an AWB is assigned. Changes are recorded in the order's edit history.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Buyer */}
          <section>
            <h4 className="mb-2 text-sm font-semibold text-slate-700">Buyer</h4>
            <div className="grid grid-cols-2 gap-3">
              {field("Name *", buyer.name || "", (v) => setBuyerField("name", v))}
              {field("Phone *", buyer.phone || "", (v) => setBuyerField("phone", v), { inputMode: "numeric" })}
              {field("Email", buyer.email || "", (v) => setBuyerField("email", v))}
              {field("Pincode *", buyer.pincode || "", (v) => setBuyerField("pincode", v), { inputMode: "numeric", maxLength: 6 })}
              <div className="col-span-2">{field("Address *", buyer.address || "", (v) => setBuyerField("address", v))}</div>
              {field("Landmark", buyer.landmark || "", (v) => setBuyerField("landmark", v))}
              {field("City", buyer.city || "", (v) => setBuyerField("city", v))}
              {field("State", buyer.state || "", (v) => setBuyerField("state", v))}
            </div>
          </section>

          {/* Products */}
          <section>
            <div className="mb-2 flex items-center justify-between">
              <h4 className="text-sm font-semibold text-slate-700">Products</h4>
              <Button type="button" size="sm" variant="outline" onClick={addProduct}><Plus className="mr-1 h-3.5 w-3.5" />Add line</Button>
            </div>
            <div className="space-y-2">
              {products.map((p, i) => (
                <div key={i} className="grid grid-cols-12 items-end gap-2 rounded-md border bg-slate-50/60 p-2">
                  <div className="col-span-4">{field("Name", p.name, (v) => setProductField(i, "name", v))}</div>
                  <div className="col-span-2">{field("Unit ₹", p.unit_price, (v) => setProductField(i, "unit_price", v), { type: "number", step: "any" })}</div>
                  <div className="col-span-2">{field("Qty", p.qty, (v) => setProductField(i, "qty", v), { type: "number", min: 1 })}</div>
                  <div className="col-span-2">{field("Disc ₹", p.discount, (v) => setProductField(i, "discount", v), { type: "number", step: "any" })}</div>
                  <div className="col-span-1">{field("Tax%", p.tax_pct, (v) => setProductField(i, "tax_pct", v), { type: "number", step: "any" })}</div>
                  <div className="col-span-1 flex justify-end pb-1">
                    <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => removeProduct(i)} disabled={products.length === 1}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Package */}
          <section>
            <h4 className="mb-2 text-sm font-semibold text-slate-700">Package</h4>
            <div className="grid grid-cols-4 gap-3">
              {field("Dead wt (kg) *", pkg.dead_weight_kg, (v) => setPkg((s) => ({ ...s, dead_weight_kg: v })), { type: "number", step: "any" })}
              {field("Length (cm) *", pkg.length_cm, (v) => setPkg((s) => ({ ...s, length_cm: v })), { type: "number", step: "any" })}
              {field("Breadth (cm) *", pkg.breadth_cm, (v) => setPkg((s) => ({ ...s, breadth_cm: v })), { type: "number", step: "any" })}
              {field("Height (cm) *", pkg.height_cm, (v) => setPkg((s) => ({ ...s, height_cm: v })), { type: "number", step: "any" })}
            </div>
          </section>

          {/* Fulfilment / money */}
          <section>
            <h4 className="mb-2 text-sm font-semibold text-slate-700">Fulfilment</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-600">Pickup location *</Label>
                <select value={pickupLocationId} onChange={(e) => setPickupLocationId(e.target.value)} className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm">
                  <option value="">Select…</option>
                  {pickupLocations.map((loc) => (
                    <option key={loc.id} value={loc.id}>{loc.facility_name || loc.name || loc.id} {loc.pincode ? `(${loc.pincode})` : ""}</option>
                  ))}
                </select>
              </div>
              {field("Reference / order no.", reference, setReference)}
              <div>
                <Label className="text-xs text-slate-600">Payment</Label>
                <select value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)} className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm">
                  <option value="prepaid">Prepaid</option>
                  <option value="cod">COD</option>
                </select>
              </div>
              {paymentMode === "cod"
                ? field("COD amount *", codAmount, setCodAmount, { type: "number", step: "any" })
                : <div />}
              <div>
                <Label className="text-xs text-slate-600">Order value</Label>
                <div className="mt-1 flex items-center gap-2">
                  <Input value={orderValue} onChange={(e) => setOrderValue(e.target.value)} className="h-9" type="number" step="any" />
                  <Button type="button" size="sm" variant="outline" className="shrink-0 text-xs" onClick={() => setOrderValue(String(computedValue))} title="Use the total computed from the product lines">= ₹{computedValue}</Button>
                </div>
              </div>
            </div>
          </section>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="button" onClick={handleSave} disabled={saving}>
            {saving ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" />Saving…</> : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default B2COrderEditDialog;
